import React, { useEffect, useMemo, useState, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery';
import { useActiveInlineCommentsQuery } from '@confluence/comments-data/entry-points/useActiveInlineCommentsQuery';
import {
	type CommentData,
	type ReplyData,
	useCommentsData,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { usePageContentId } from '@confluence/page-context';
import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { getVisibleCommentThreads } from '@confluence/comments-panel-utils/entry-points/getVisibleCommentThreads';
import {
	LocationValue,
	getAnnotationThreadKeys,
	getUnreadCommentThreads,
	onlyHasReactionsSummaryErrors,
} from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import {
	ViewValues,
	useCommentsPanel,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';

import { usePanelRefresh } from '../hooks/usePanelRefresh';
import { SIDEBAR_COMMENTS_PANEL_LOAD_UNREAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';

import { Loading } from '../components/Loading';
import { ErrorPanel } from '../components/ErrorPanel';
import { CommentsPanelList } from '../components/CommentsPanelList';
import { EmptyUnreadSVG } from '../assets/EmptyUnreadSVG';
import { EmptyStateComponent } from '../components/EmptyStateComponent';
import { useCommentsPanelMetrics } from '../hooks/useCommentsPanelMetrics';
import { COMMENTS_PANEL_UNREAD_VIEW_METRIC } from '../perf.config';

import {
	DEFAULT_TOP_LEVEL_ACTIONS,
	DEFAULT_TOP_LEVEL_ACTIONS_WITHOUT_DELETE,
} from './topLevelActions';

type UnreadViewProps = {
	showDeleteOption: boolean;
	flags?: FlagsStateContainer;
	// Disable loading UI for VR tests
	disabledLoading?: boolean;
};

const i18n = defineMessages({
	emptyCommentsHeaderText: {
		id: 'comments-panel.empty.unread.comments.header.text',
		defaultMessage: 'No unread comments',
		description:
			'Header text to display in Unread View empty state when there are no unread comments on the page.',
	},
	emptyCommentsBodyText: {
		id: 'comments-panel.empty.unread.comments.body.text',
		defaultMessage: `Comments you haven’t read yet will appear here.`,
		description:
			'Body text to display in Unread View empty state when there are no unread comments on the page.',
	},
});

const isCommentValid = (unreadIds: Set<string>) => (comment: CommentData | ReplyData) => {
	// Never show comments that were removed by another user
	if (Boolean(comment.wasRemovedByAnotherUser)) return false;

	// Reply comments: Always show them (unless removed)
	if (!('replies' in comment)) return true;

	// Top-level comments: Show if the comment or any of its replies are unread
	const isCommentUnread = unreadIds.has(comment.id);
	const hasUnreadReplies = comment.replies.some((reply) => unreadIds.has(reply.id));

	return isCommentUnread || hasUnreadReplies;
};

export const UnreadView = ({
	showDeleteOption,
	flags,
	disabledLoading = false,
}: UnreadViewProps) => {
	const { unreadCommentsListState, unreadCommentIDSet, shouldClearVisibleThreads } =
		useUnreadCommentsState();
	const { formatMessage } = useIntl();
	const [
		{ orderedActiveAnnotationIdList, commentsDataMap, removedThreadsMap },
		{ clearRemovedComments, clearCommentsData },
	] = useCommentsData();
	const [
		{ initialDataLoadedForViewMap, showInlineComments, showGeneralComments, currentView },
		{ setInitialDataLoadedForView },
	] = useCommentsPanel();
	const isInitialCommentDataLoaded = initialDataLoadedForViewMap[ViewValues.UNREAD];

	const { commentService } = useCommentService();
	const [contentId] = usePageContentId();
	const pageMode = useGetPageMode();
	const [allCommentsRendered, setAllCommentsRendered] = useState(false);
	const [commentBatchRendered, setCommentBatchRendered] = useState(false);

	const unreadAnnotationsToLoad = useMemo(() => {
		const unreadCommentsThreadKeys = new Set([
			...unreadCommentsListState.map((unreadComment) => unreadComment.threadKey),
		]);
		// Filter and map to get the annotation IDs that need to be loaded
		return orderedActiveAnnotationIdList
			.filter(
				(annotation) => !annotation.isLoaded && unreadCommentsThreadKeys.has(annotation.threadKey),
			)
			.map((annotation) => annotation.threadKey);
	}, [unreadCommentsListState, orderedActiveAnnotationIdList]);

	const commentsQueryHook = fg('confluence_frontend_comments_panel_v2')
		? useActiveCommentsQuery
		: useActiveInlineCommentsQuery;
	const commentsQueryHookVariables: any = fg('confluence_frontend_comments_panel_v2')
		? {
				pageId: contentId || '',
			}
		: {
				pageId: contentId || '',
				markerRefList: unreadAnnotationsToLoad,
				skip: unreadAnnotationsToLoad.length === 0,
			};

	const { loading, error, refetch, data } = commentsQueryHook(commentsQueryHookVariables);
	const { shouldSuppressEmptyState, shouldShowLoading } = usePanelRefresh({
		refetch,
		clearCommentsData,
		registerTriggerCommentPanelRefresh: (handler) =>
			commentService.registerTriggerCommentPanelRefresh(handler),
		queryLoading: loading,
		disabled: disabledLoading,
	});

	useEffect(() => {
		// When this view unmounts, we want to clear out the "resolved"/"deleted" comments
		return () => {
			clearRemovedComments();
		};
	}, [clearRemovedComments]);

	useEffect(() => {
		if (!loading) {
			setInitialDataLoadedForView({ viewToSetLoaded: ViewValues.UNREAD });
		}
	}, [loading, setInitialDataLoadedForView]);

	// refresh lifecycle managed by usePanelRefresh

	// Track unread IDs that persist even after comments are read
	const [unreadIds, setUnreadIds] = useState(() => new Set(unreadCommentIDSet));
	const lastUnreadIdsSize = useRef(unreadCommentIDSet.size);

	useEffect(() => {
		if (shouldClearVisibleThreads) {
			setUnreadIds(new Set());
		} else if (unreadCommentIDSet.size > lastUnreadIdsSize.current) {
			setUnreadIds((prevUnreadIds) => new Set([...prevUnreadIds, ...unreadCommentIDSet]));
		}
		lastUnreadIdsSize.current = unreadCommentIDSet.size;
	}, [unreadCommentIDSet, shouldClearVisibleThreads]);

	const commentThreads = useMemo(() => {
		if (
			(!isInitialCommentDataLoaded && loading) ||
			(orderedActiveAnnotationIdList.length === 0 &&
				Object.keys(commentsDataMap.general).length === 0 &&
				Object.keys(removedThreadsMap.inline).length === 0 &&
				Object.keys(removedThreadsMap.general).length === 0 &&
				unreadCommentsListState.length === 0)
		) {
			return [];
		}

		return fg('confluence_frontend_content_wrapper')
			? getVisibleCommentThreads({
					currentView,
					commentsDataMap,
					includeGeneralComments: showGeneralComments,
					includeInlineComments: showInlineComments,
					orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
					isCommentValid: isCommentValid(unreadIds),
				})
			: getUnreadCommentThreads({
					numUnreadComments: unreadCommentsListState?.length,
					commentsDataMap,
					orderedActiveAnnotationIdList,
					removedThreadsMap,
					location: LocationValue.PANEL,
					showInlineComments,
					showGeneralComments,
				});
	}, [
		loading,
		isInitialCommentDataLoaded,
		commentsDataMap,
		orderedActiveAnnotationIdList,
		removedThreadsMap,
		unreadCommentsListState?.length,
		showInlineComments,
		showGeneralComments,
		currentView,
		unreadIds,
	]);

	useCommentsPanelMetrics({
		loading,
		error,
		allCommentsRendered,
		commentBatchRendered,
		totalComments: fg('confluence_frontend_abort_panel_exp_if_no_comment')
			? commentThreads.length
			: data?.comments?.nodes?.length,
		contentId,
		pageMode,
		experienceName: SIDEBAR_COMMENTS_PANEL_LOAD_UNREAD_EXPERIENCE,
		metric: COMMENTS_PANEL_UNREAD_VIEW_METRIC,
	});

	if (shouldShowLoading(isInitialCommentDataLoaded, loading)) {
		return <Loading />;
	}

	if (
		error &&
		(!onlyHasReactionsSummaryErrors(error) || !fg('confluence_frontend_handle_reactions_errors'))
	) {
		return (
			<ErrorPanel
				error={error}
				onRetryClick={async () => {
					await refetch();
				}}
			/>
		);
	}

	if (commentThreads.length === 0 && !shouldSuppressEmptyState()) {
		return (
			<EmptyStateComponent
				SVG={EmptyUnreadSVG}
				emptyCommentsHeaderText={formatMessage(i18n.emptyCommentsHeaderText)}
				emptyCommentsBodyText={formatMessage(i18n.emptyCommentsBodyText)}
				onRendered={
					fg('confluence_frontend_abort_panel_exp_if_no_comment')
						? () => {
								setCommentBatchRendered(true);
								setAllCommentsRendered(true);
							}
						: undefined
				}
			/>
		);
	}

	return (
		<CommentsPanelList
			commentThreads={commentThreads}
			supportedTopLevelActions={
				showDeleteOption ? DEFAULT_TOP_LEVEL_ACTIONS : DEFAULT_TOP_LEVEL_ACTIONS_WITHOUT_DELETE
			}
			flags={flags}
			onAllCommentsRendered={() => setAllCommentsRendered(true)}
			onCommentBatchRendered={() => setCommentBatchRendered(true)}
		/>
	);
};
